import {createContext, useContext} from "react"
import {PublicKey} from "@solana/web3.js"
import bs58 from "bs58"
import {useProgram} from "@/context/ProgramContext.tsx"
import {IdlAccounts} from "@coral-xyz/anchor"
import {ZenexPools} from "@/components/SolanaPools/anchor-idl/idl.ts"
import * as anchor from "@coral-xyz/anchor"

export type GetAllUserPools = (userPub58: string) => Promise<{
  publicKey: PublicKey,
  account: IdlAccounts<ZenexPools>["userState"]
}[]>

export type GetAllPoolsState = () => Promise<{
  publicKey: PublicKey,
  account: IdlAccounts<ZenexPools>["zenexPoolState"]
}[]>
export type GetDepositStat = (regionId: number,year: number, month: number, userPub58: string) => Promise<IdlAccounts<ZenexPools>["userState"]>
export type GetPoolState = (regionId: number,year: number, month: number) => Promise<IdlAccounts<ZenexPools>["zenexPoolState"]>

type UserPoolsContextType = {
  getAllUserPools: GetAllUserPools
  getAllPoolsState: GetAllPoolsState
  getDepositStat: GetDepositStat
  getPoolState: GetPoolState
  getAllPoolUsers:(regionId: number, year: number, month: number) => Promise<{publicKey: PublicKey,account:IdlAccounts<ZenexPools>["userState"] }[]>
}

const UserPoolsContext = createContext<UserPoolsContextType | undefined>(undefined)

export const UserPoolsProvider = ({children}: { children: React.ReactNode }) => {
  const {program} = useProgram()

  // вернет статистику участия всех пулов в которых участвует пользователь
  const getAllUserPools = async (userPub58: string) => {
    if (!program) throw new Error("Program is not initialized")
    return await program.account.userState.all([
      {
        memcmp: {
          offset: 8,
          bytes: userPub58,
        },
      },
    ])
  }

  const getAllPoolsState = async () => {
    if (!program) throw new Error("Program is not initialized")
    return await program.account.zenexPoolState.all()
  }

  // Получение статистики участия в конкретном пуле по году и месяцу
  const getDepositStat = async (regionId: number ,year: number, month: number, userPub58: string) => {
    if (!program) throw new Error("Program is not initialized")
    const pda = PublicKey.findProgramAddressSync(
      [
        Buffer.from("user"),
        bs58.decode(userPub58),
        Buffer.from([regionId]), //todo regionId
        Buffer.from(Uint16Array.of(year).buffer),
        Buffer.from([month]),
      ],
      program.programId
    )[0]
    return program.account.userState.fetch(pda)
  }

  // функция для получения PDA пула по году и месяцу
  const poolPda = (regionId: number, year: number, month: number, programId: PublicKey): PublicKey => {
    if (!program) throw new Error("Program is not initialized")

    return anchor.web3.PublicKey.findProgramAddressSync(
      [
        Buffer.from("pools"),
        Buffer.from([regionId]), //todo regionId
        Buffer.from(Uint16Array.of(year).buffer), // u16 little-endian
        Buffer.from([month]), // u8
      ],
      programId
    )[0]
  }

  // возвращается информацию по конкретному пулу за год и месяц
  const getPoolState = async (regionId:number, year: number, month: number) => {
    if (!program) throw new Error("Program is not initialized")

    const pda = poolPda(regionId, year, month, program.programId)
    return await program.account.zenexPoolState.fetch(pda)
  }

  function getAllPoolUsers (regionId:number, year: number, month: number) {
    if (!program) throw new Error("Program is not initialized")

    const data = program.account.userState.all([

      {
        memcmp: {
          offset: 40,
          bytes: bs58.encode(Buffer.from([regionId])),
        }
      },
      {
        memcmp: {
          offset: 41,
          bytes: bs58.encode(Buffer.from(Uint16Array.of(year).buffer)),
        }
      },
      {
        memcmp: {
          offset: 43,
          bytes: bs58.encode(Buffer.from([month])),
        }
      }

    ]);
    return data as  Promise<{publicKey: PublicKey,account:IdlAccounts<ZenexPools>["userState"] }[]>;
  }

  return (
    <UserPoolsContext.Provider value={{getAllUserPools, getAllPoolsState, getDepositStat, getPoolState, getAllPoolUsers}}>
      {children}
    </UserPoolsContext.Provider>
  )
}

export const useUserPools = () => {
  const ctx = useContext(UserPoolsContext)
  if (!ctx) throw new Error("useUserPools must be used within a UserPoolsProvider")
  return ctx
}
